"use client"

import { useRef, useState, useCallback, useEffect } from "react"
import { X } from "lucide-react"
import { GRASS_BLADES } from "@/components/guides/grass-blades"

const GrassBlade = GRASS_BLADES["kentucky-bluegrass"]
import { useIsMobile } from "@/components/ui/use-mobile"
import { cn } from "@/lib/utils"
import { ChatMessages } from "./chat-messages"
import { ChatInput } from "./chat-input"
import type { ChatMessage, ChatError } from "@/hooks/use-chat"
import { useModalFocusManagement } from "@/hooks/use-modal-focus-management"

interface ChatPanelProps {
  isOpen: boolean
  onClose: () => void
  messages: ChatMessage[]
  activeTools: string[]
  isStreaming: boolean
  error: ChatError | null
  onSend: (content: string) => void
  onClearError: () => void
}

export function ChatPanel({
  isOpen,
  onClose,
  messages,
  activeTools,
  isStreaming,
  error,
  onSend,
  onClearError,
}: ChatPanelProps) {
  const isMobile = useIsMobile()
  const panelRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const isDragging = useRef(false)
  const startY = useRef(0)
  const currentY = useRef(0)

  const handleDragStart = useCallback((e: React.TouchEvent) => {
    isDragging.current = true
    startY.current = e.touches[0].clientY
    currentY.current = e.touches[0].clientY
  }, [])

  const handleDragMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return
    currentY.current = e.touches[0].clientY
    const diff = currentY.current - startY.current
    if (diff > 0 && panelRef.current) {
      const resistance = 0.5
      panelRef.current.style.transform = `translateY(${diff * resistance}px)`
      panelRef.current.style.transition = 'none'
    }
  }, [])

  const handleDragEnd = useCallback(() => {
    if (!isDragging.current) return
    isDragging.current = false
    if (panelRef.current) {
      panelRef.current.style.transition = ''
      panelRef.current.style.transform = ''
      const diff = currentY.current - startY.current
      if (diff > 100) {
        onClose()
      }
    }
  }, [onClose])

  // Reposition panel above the virtual keyboard on mobile (iOS Safari fix)
  useEffect(() => {
    if (!isMobile || !isOpen) return

    const vv = window.visualViewport
    if (!vv) return

    const handleResize = () => {
      const panel = panelRef.current
      if (!panel) return

      // When the keyboard opens, visualViewport.height shrinks but
      // window.innerHeight stays the same (layout viewport). The difference
      // is the keyboard height.
      const keyboardHeight = window.innerHeight - vv.height - vv.offsetTop

      if (keyboardHeight > 100) {
        // Keyboard is open — move panel above it and fill visible area
        panel.style.bottom = `${keyboardHeight}px`
        panel.style.height = `${vv.height}px`
      } else {
        // Keyboard closed — revert to CSS defaults
        panel.style.bottom = ''
        panel.style.height = ''
      }
    }

    vv.addEventListener('resize', handleResize)
    vv.addEventListener('scroll', handleResize)

    const panelAtMount = panelRef.current
    return () => {
      vv.removeEventListener('resize', handleResize)
      vv.removeEventListener('scroll', handleResize)
      if (panelAtMount) {
        panelAtMount.style.bottom = ''
        panelAtMount.style.height = ''
      }
    }
  }, [isMobile, isOpen])

  // The parent (`chat-widget.tsx`) now mount-gates the panel on
  // `isOpen || isClosing`, so we drive the open/close transition off `isOpen`
  // while the element exists. We start at the closed transform and flip to
  // open on the first paint, so the slide-in still animates.
  const [animateIn, setAnimateIn] = useState(false)
  useEffect(() => {
    if (isOpen) {
      const id = requestAnimationFrame(() => setAnimateIn(true))
      return () => cancelAnimationFrame(id)
    }
    setAnimateIn(false)
  }, [isOpen])

  useModalFocusManagement({
    isOpen,
    containerRef: panelRef,
    initialFocusRef: closeButtonRef,
    onEscape: onClose,
    isolate: isMobile,
    trapFocus: isMobile,
    restoreFocus: false,
  })

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className={cn(
            "fixed inset-0 bg-black/40 z-59 transition-opacity duration-300",
            animateIn ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        id="lawn-care-chat-dialog"
        ref={panelRef}
        role="dialog"
        aria-modal={isMobile ? "true" : undefined}
        aria-hidden={!isOpen ? "true" : undefined}
        inert={!isOpen ? true : undefined}
        aria-labelledby="lawn-care-chat-title"
        aria-describedby="lawn-care-chat-description"
        tabIndex={-1}
        className={cn(
          "lcw-panel fixed z-60 flex flex-col transition-all duration-300 overflow-hidden",
          isMobile
            ? "inset-x-0 bottom-0 w-full h-[85dvh]"
            : "bottom-24 right-6 w-[400px] h-[600px]",
          animateIn
            ? "opacity-100 translate-y-0"
            : isMobile
              ? "opacity-0 translate-y-full"
              : "opacity-0 translate-y-4"
        )}
        style={{ borderRadius: isMobile ? "26px 26px 0 0" : "26px" }}
      >
        {/* Drag handle — mobile only */}
        {isMobile && (
          <div
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
        )}

        {/* Header */}
        <div className="lcw-head">
          <span className="lcw-avatar">
            <GrassBlade className="w-7 h-7" color="#15803d" />
          </span>
          <div className="lcw-head__meta">
            <h2 id="lawn-care-chat-title" className="lcw-head__name">Lawn Care Expert</h2>
            <div id="lawn-care-chat-description" className="lcw-head__status">
              <span className="dot" />
              Ask a lawn care question
            </div>
          </div>
          <button ref={closeButtonRef} className="lcw-x" onClick={onClose} aria-label="Close chat">
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <ChatMessages
          messages={messages}
          activeTools={activeTools}
          error={error}
          onSend={onSend}
          onClearError={onClearError}
        />

        {/* Input */}
        <ChatInput onSend={onSend} disabled={isStreaming} />
      </div>
    </>
  )
}
